import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Layout from "../components/Layout";
import API from "../api/axios";
import usePlan from "../hooks/usePlan";
import UpgradePrompt from "../components/UpgradePrompt";
import { formatDate, saveToCache, loadFromCache } from "../utils/cache";
import SaleReceipt from "../components/SaleReceipt";
import { useTheme } from "../context/ThemeContext";

export default function Sales() {
  const { theme } = useTheme();
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [items, setItems] = useState([{ product_id: "", quantity: 1 }]);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [saleDate, setSaleDate] = useState("");
  const { isPaid, isExpired } = usePlan();
  const [customerPhone, setCustomerPhone] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [search, setSearch] = useState("");
  const [viewReceipt, setViewReceipt] = useState(null);
  const [isCredit, setIsCredit] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [dueDate, setDueDate] = useState("");

  // Shared theme styles
  const card = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "14px",
    padding: "32px",
    marginBottom: "32px",
    boxShadow: theme.cardShadow,
  };
  const input = {
    background: theme.inputBg,
    border: `1px solid ${theme.inputBorder}`,
    borderRadius: "10px",
    padding: "12px 16px",
    color: theme.textPrimary,
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const fetchData = async () => {
    if (!navigator.onLine) {
      const cachedProducts = loadFromCache("ss_cache_products");
      const cachedSales = loadFromCache("ss_cache_sales");
      if (cachedProducts) setProducts(cachedProducts);
      if (cachedSales) setSales(cachedSales);
      setLoading(false);
      return;
    }
    try {
      const [productsRes, salesRes] = await Promise.all([
        API.get("/products/"),
        API.get("/sales/"),
      ]);
      if (productsRes.data) { setProducts(productsRes.data); saveToCache("ss_cache_products", productsRes.data); }
      if (salesRes.data) { setSales(salesRes.data); saveToCache("ss_cache_sales", salesRes.data); }
    } catch {
      const cachedProducts = loadFromCache("ss_cache_products");
      const cachedSales = loadFromCache("ss_cache_sales");
      if (cachedProducts) setProducts(cachedProducts);
      if (cachedSales) setSales(cachedSales);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const handleSync = () => { fetchData(); };
    window.addEventListener("martsense-sync-complete", handleSync);
    return () => window.removeEventListener("martsense-sync-complete", handleSync);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addItem = () => setItems([...items, { product_id: "", quantity: 1 }]);
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index));
  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const calcTotal = () => items.reduce((sum, item) => {
    const product = products.find((p) => p.id === item.product_id);
    return sum + (product ? product.unit_price * item.quantity : 0);
  }, 0);

  const resetForm = () => {
    setItems([{ product_id: "", quantity: 1 }]);
    setPaymentMethod("cash");
    setSaleDate("");
    setCustomerPhone("");
    setSearch("");
    setShowForm(false);
    setIsCredit(false);
    setCustomerName("");
    setDueDate("");
    setIsCredit(false);
    setCustomerName("");
    setDueDate("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.some((i) => !i.product_id)) { toast.error("Please select a product for each item"); return; }
    setSubmitting(true);

    const saleTotal = calcTotal();
    const saleItems = items.map(i => {
      const product = products.find(p => p.id === i.product_id);
      return {
        product_id: i.product_id,
        product_name: product?.name || "Item",
        quantity: parseInt(i.quantity),
        unit_price: product?.unit_price || 0,
        subtotal: (product?.unit_price || 0) * parseInt(i.quantity),
      };
    });

    const payload = {
      payment_method: paymentMethod,
      sale_date: saleDate || null,
      customer_phone: customerPhone || null,
      items: items.map((i) => ({ product_id: i.product_id, quantity: parseInt(i.quantity) })),
      is_credit: isCredit,
      customer_name: customerName || null,
      due_date: dueDate || null,
    };

    if (!navigator.onLine) {
      try {
        const { addToQueue } = await import("../utils/offlineQueue");
        await addToQueue({ method: "POST", url: "/sales/", data: payload });
        resetForm();
        toast.success("Sale saved offline — will sync when connected", { icon: "📵" });
      } catch { toast.error("Failed to save offline"); }
      finally { setSubmitting(false); }
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const token = localStorage.getItem("token");
      const res = await fetch("https://stocksense-backend-g1kp.onrender.com/sales/", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      if (res.ok) {
        resetForm();
        toast.success("Sale recorded successfully");
        setTimeout(() => setReceipt({ id: "new", items: saleItems, created_at: new Date().toISOString(), total_amount: saleTotal, payment_method: paymentMethod, customer_phone: customerPhone || null }), 100);
        setTimeout(() => fetchData(), 1000);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to record sale");
      }
    } catch (err) {
      if (err.name === "AbortError") toast.error("Request timed out. Check your connection and try again.");
      else toast.error("Failed to record sale. Please try again.");
    } finally { setSubmitting(false); }
  };

  const handleExportSales = async () => {
    try {
      const response = await API.get("/reports/export/sales", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "sales-export.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Sales exported successfully");
    } catch (err) { toast.error(err.response?.data?.detail || "Export failed"); }
  };

  return (
    <Layout>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <div>
          <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>Sales</h1>
          <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>{sales.length} total transactions</p>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {isPaid && (
            <button onClick={handleExportSales} style={{
              background: "none", border: "1px solid #2ec4b6",
              borderRadius: "10px", padding: "12px 20px",
              color: "#2ec4b6", fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}>⬇ Export CSV</button>
          )}
          <button onClick={() => setShowForm(!showForm)} style={{
            background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
            color: "#0a0f0f", border: "none", borderRadius: "10px",
            padding: "12px 24px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
          }}>
            {showForm ? "Cancel" : "+ Record Sale"}
          </button>
        </div>
      </div>

      {isExpired && (
        <UpgradePrompt
          feature="Sales Recording"
          message="Subscribe to continue recording sales and access your full sales history."
        />
      )}

      {/* Sale Form */}
      {showForm && (
        <div style={card}>
          <h2 style={{ fontSize: "18px", fontWeight: "600", color: theme.textPrimary, marginBottom: "24px" }}>New Sale</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Search Product</label>
              <input
                type="text" placeholder="🔍 Type to filter products..."
                value={search} onChange={(e) => setSearch(e.target.value)} style={input}
              />
            </div>

            {items.map((item, index) => (
              <div key={index} style={{ display: "flex", gap: "16px", alignItems: "flex-end", marginBottom: "16px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                  <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Product</label>
                  <select
                    value={item.product_id}
                    onChange={(e) => { updateItem(index, "product_id", e.target.value); setSearch(""); }}
                    style={input} required
                  >
                    <option value="">Select product</option>
                    {products
                      .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} — ₦{p.unit_price.toLocaleString()} (Stock: {p.quantity_in_stock})
                        </option>
                      ))}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "120px" }}>
                  <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Qty</label>
                  <input
                    type="number" min="1" value={item.quantity}
                    onChange={(e) => updateItem(index, "quantity", e.target.value)}
                    style={input} required
                  />
                </div>
                {items.length > 1 && (
                  <button type="button" onClick={() => removeItem(index)} style={{
                    background: "none", border: "1px solid #e74c3c",
                    borderRadius: "8px", color: "#e74c3c",
                    padding: "12px", fontSize: "14px", cursor: "pointer",
                  }}>✕</button>
                )}
              </div>
            ))}

            <button type="button" onClick={addItem} style={{
              background: "none", border: `1px dashed ${theme.cardBorder}`,
              borderRadius: "10px", color: theme.textSecondary,
              padding: "10px 20px", fontSize: "14px",
              marginBottom: "24px", cursor: "pointer",
            }}>+ Add Another Item</button>

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Customer Phone (optional)</label>
              <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="+234 800 000 0000" style={input} />
            </div>

            {/* Credit Sale Toggle */}
<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 0", borderTop: `1px solid ${theme.divider}` }}>
  <div>
    <p style={{ fontSize: "14px", fontWeight: "600", color: theme.textPrimary, marginBottom: "4px" }}>
      Sold on Credit
    </p>
    <p style={{ fontSize: "12px", color: theme.textSecondary }}>
      Customer will pay later
    </p>
  </div>
  <div onClick={() => setIsCredit(!isCredit)} style={{
    width: "48px", height: "26px", borderRadius: "13px",
    background: isCredit ? "#f5a623" : theme.divider,
    cursor: "pointer", position: "relative", transition: "background 0.2s",
    flexShrink: 0, marginLeft: "16px",
  }}>
    <div style={{
      position: "absolute", top: "3px",
      left: isCredit ? "25px" : "3px",
      width: "20px", height: "20px", borderRadius: "50%",
      background: "#ffffff", transition: "left 0.2s",
    }} />
  </div>
</div>

{/* Credit fields — only show when toggle is on */}
{isCredit && (
  <>
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
      <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>
        Customer Name *
      </label>
      <input
        type="text"
        value={customerName}
        onChange={(e) => setCustomerName(e.target.value)}
        placeholder="e.g. Ade Furniture Shop"
        required={isCredit}
        style={input}
      />
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
      <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>
        Due Date (optional)
      </label>
      <input
        type="date"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
        min={new Date().toISOString().split("T")[0]}
        style={input}
      />
    </div>
    <div style={{
      background: "#2e1a00", border: "1px solid #f5a62333",
      borderRadius: "10px", padding: "12px 16px", marginBottom: "16px",
    }}>
      <p style={{ fontSize: "13px", color: "#f5a623" }}>
        ⚠️ This sale will be marked as credit. Track repayment in the Debts section.
      </p>
    </div>
  </>
)}

            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "24px" }}>
              <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Sale Date (leave blank for today)</label>
              <input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} max={new Date().toISOString().split("T")[0]} style={input} />
            </div>

            <div style={{ display: "flex", alignItems: "flex-end", gap: "24px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "13px", fontWeight: "500", color: theme.textSecondary }}>Payment Method</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} style={{ ...input, width: "200px" }}>
                  <option value="cash">Cash</option>
                  <option value="transfer">Bank Transfer</option>
                  <option value="card">Card</option>
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <span style={{ fontSize: "12px", color: theme.textSecondary }}>Total</span>
                <span style={{ fontSize: "24px", fontWeight: "700", color: "#2ec4b6" }}>₦{calcTotal().toLocaleString()}</span>
              </div>
              <button type="submit" disabled={submitting} style={{
                background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                color: "#0a0f0f", border: "none", borderRadius: "10px",
                padding: "12px 32px", fontSize: "14px", fontWeight: "700", cursor: "pointer",
              }}>
                {submitting ? "Recording..." : "Record Sale"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sales Table */}
      {loading ? (
        <p style={{ color: theme.textSecondary }}>Loading...</p>
      ) : sales.length === 0 ? (
        <div style={{ textAlign: "center", color: theme.textSecondary, padding: "60px 0" }}>
          <p>No sales recorded yet.</p>
        </div>
      ) : (
        <div style={{
          background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
          borderRadius: "14px", overflowX: "auto",
          WebkitOverflowScrolling: "touch", boxShadow: theme.cardShadow,
        }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "500px" }}>
            <thead>
              <tr>
                {["Date", "Product", "Amount", "Payment", "Status", "Receipt"].map((h) => (
                  <th key={h} style={{
                    textAlign: "left", padding: "14px 16px",
                    fontSize: "12px", fontWeight: "600",
                    color: theme.textSecondary, textTransform: "uppercase",
                    letterSpacing: "0.5px", borderBottom: `1px solid ${theme.divider}`,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => (
                <tr key={sale.id} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>
                    {formatDate(sale.created_at, true)}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "12px", color: theme.textSecondary }}>
                    {sale.items?.length > 0 ? sale.items.map(i => i.product_name).join(", ") : "—"}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: "#2ec4b6", fontWeight: 600 }}>
                    ₦{sale.total_amount.toLocaleString()}
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary, textTransform: "capitalize" }}>
                    {sale.payment_method}
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={{
                      background: sale.status === "completed" ? "#0d2e1a" : "#2e0d0d",
                      color: sale.status === "completed" ? "#2ecc71" : "#e74c3c",
                      padding: "4px 10px", borderRadius: "20px",
                      fontSize: "12px", fontWeight: 600,
                    }}>{sale.status}</span>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <button onClick={() => setViewReceipt(sale)} style={{
                      background: "none", border: "1px solid #2ec4b6",
                      borderRadius: "6px", color: "#2ec4b6",
                      padding: "6px 10px", fontSize: "12px",
                      fontWeight: "600", cursor: "pointer",
                    }}>🧾 View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewReceipt && <SaleReceipt sale={viewReceipt} onClose={() => setViewReceipt(null)} />}
      {receipt && <SaleReceipt sale={receipt} onClose={() => setReceipt(null)} />}
    </Layout>
  );
}